<template>
  <div
    :class="{
      'search-input__focus': show
    }"
    class="search-input"
    v-click-outside="hideSuggestions"
  >
    <div class="input-wrapper">
      <button
        v-if="value"
        class="input-wrapper--button-clear"
        type="button"
        @click.stop.prevent="clearInput"
      >
        <Icon icon="x-icon" />
      </button>
      <input
        :value="value"
        :placeholder="placeholder"
        ref="search-input-field"
        type="text"
        @input="$emit('input', $event.target.value)"
        @keypress="$emit('keypress', $event)"
        @focus="show = true"
      />
      <Icon icon="search" class="input-wrapper--icon-search" />
    </div>

    <!-- SUGESTÕES -->
    <transition name="drop-down" appear>
      <div
        v-if="show && filteredSuggestions.length > 0"
        class="search-input--suggestions"
      >
        <slot
          name="suggestions"
          :suggestions="{ filtered: filteredSuggestions, all: suggestions }"
        >
          <Button
            v-for="(item, index) in filteredSuggestions"
            :ref="`search-input--suggestions-button_${index + 1}`"
            :key="`${item.text}_${index + 1}`"
            :class="{ 'suggestion-button__active': index + 1 === currentIndex }"
            class="suggestion-button"
            @keypress.enter="listItemSelectionHandler(item, $event)"
            @click="listItemSelectionHandler(item, $event)"
          >
            <span v-html="formatedValue(item.text)"></span>
          </Button>
        </slot>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Input de busca usado no topo das páginas
 */
import vClickOutside from 'v-click-outside'
import Button from './Button'
import { getHighlightedText } from '@/helpers/string'

export default {
  name: 'SearchInput',
  components: {
    Button
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    /** valor do input */
    value: {
      type: String,
      default: ''
    },
    /** placeholder do input */
    placeholder: {
      type: String,
      default: 'Buscar'
    },
    /**
     * sugestões de resultados procurados que devem aparecer
     *
     * Obrigatório o objeto ter uam propriedade "text"
     * Ex:
     * {
     *   text: "sugestão 1",
     *   link: "/caminho/"
     * }
     */
    suggestions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      show: false /** flag para controlar a renderização */,
      currentIndex: 0 /** índice usado para destacar o item quando usada a navegação pelo teclado */
    }
  },
  computed: {
    filteredSuggestions() {
      return this.suggestions.filter((item) => {
        if (this.value.trim() === '') return false
        return item.text.toLowerCase().indexOf(this.value.toLowerCase()) > -1
      })
    }
  },
  watch: {
    value(newVal) {
      /** se não possuí um valor, zera o indíce atual da lista de sugestões
       * seta a flag de renderização para false
       * remove o evento para navegação na lista de opções
       */
      if (!newVal) {
        this.currentIndex = 0
        this.show = false
        this.removeNavigationListener()
        this.$refs['search-input-field'].focus()
      } else {
        this.show = true
        this.addNavigationListener()
      }
    },
    show(newValue) {
      /** remove o evento para navegação na lista de opções */
      if (!newValue) {
        this.removeNavigationListener()
      } else {
        this.addNavigationListener()
      }
    }
  },
  beforeDestroy() {
    this.removeNavigationListener()
  },
  methods: {
    formatedValue(key) {
      if (this.value.trim() !== '') {
        return getHighlightedText(this.value, key)
      }

      return this.value
    },
    hideSuggestions() {
      this.show = false
      this.currentIndex = 0
    },
    listNavigationHandler(e) {
      if (e.keyCode === 38 && this.currentIndex > 1) {
        this.currentIndex--
      } else if (
        e.keyCode === 40 &&
        this.currentIndex < this.filteredSuggestions.length
      ) {
        this.currentIndex++
      }

      if (this.currentIndex > 0) {
        this.$refs[
          `search-input--suggestions-button_${this.currentIndex}`
        ][0].$el
          .querySelector('button')
          .focus()
      }
    },
    listItemSelectionHandler(item, event) {
      this.$emit('selectItem', { item, event })
    },
    addNavigationListener() {
      document.addEventListener('keydown', this.listNavigationHandler)
    },
    removeNavigationListener() {
      document.removeEventListener('keydown', this.listNavigationHandler)
    },
    clearInput() {
      this.$emit('input', '')
      this.$emit('clear')
    }
  }
}
</script>

<style lang="scss" scoped>
.search-input {
  position: relative;
  border-bottom: 1px solid $gray-darkest;

  &__focus {
    border-color: $primary;
    border-width: 2px;
  }

  .input-wrapper {
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;

    &--button-clear {
      width: auto;
      margin: 0;
      margin-right: 10px;
      padding: 0;
      border: none;
      background: none;

      .icon {
        width: 12px;
        height: 12px;
        color: $gray-mid;
      }
    }

    &--icon {
      &-search {
        width: 19px;
        height: 19px;
      }
    }
  }

  input {
    width: 100%;
    height: $input-height;
    border: none;
    outline: none;
    font-size: font-size('size-medium');
  }

  &--suggestions {
    width: 100%;
    position: absolute;
    z-index: 3;
    top: calc(100% + 3px);
    left: 0;
    background-color: $white;
    @include drop-shadow(0px 20px 40px rgba(0, 0, 0, 0.15));

    .suggestion-button {
      ::v-deep .button {
        height: 45px;
        background-color: $white;
        color: $gray-darkest;
        border-radius: 0;
        justify-content: flex-start;

        &:hover {
          filter: none;
          background-color: #{$gray-mid}#{26};
        }
      }

      &__active {
        ::v-deep .button {
          filter: none;
          background-color: #{$gray-mid}#{26};
        }
      }
    }
  }
}
</style>
